// src/pages/personal/analytics/DashboardSection/components/TimeDistribution/TimeDistribution.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Grid } from '@/components/ui/Layout';
import { COLORS } from '@/theme';
import { BarChart } from '@/components/ui/Charts';
import { formatTime, formatPercent } from '@/utils/Formatters';
import { LOCALES } from '@/utils/constants';
import { useResponsive } from '@/hooks/useResponsive';
import styles from '../DashboardSection.module.css';
import { PeakHour, PeakDay } from '../../types/types';

interface TimeDistributionProps {
  peakHours: PeakHour[];
  peakDays: PeakDay[];
  isLoading?: boolean;
  displayHorizontal?: boolean;
  businessTimezone?: string;
}

const TimeDistribution: React.FC<TimeDistributionProps> = ({
  peakHours,
  peakDays,
  isLoading = false,
  displayHorizontal = false,
  businessTimezone = 'UTC'
}) => {
  const { t, i18n } = useTranslation('dashboard');
  const { isMobile, isTablet } = useResponsive();
  
  // Безопасное определение локали
  const currentLocale = i18n.language.toUpperCase() === 'RU' 
    ? LOCALES.RU 
    : i18n.language.toUpperCase() === 'EN' 
      ? LOCALES.EN 
      : i18n.language.toUpperCase() === 'KZ' 
        ? LOCALES.KZ 
        : LOCALES.RU;
  
  // Форматирование часов для отображения (учитываем часовой пояс бизнеса)
  const formatHourLabel = (hour: number): string => {
    // Создаем объект Date с указанным часом
    const now = new Date();
    now.setHours(hour, 0, 0, 0);
    const isoString = now.toISOString();
    
    // Если мобильное устройство, используем короткий формат
    if (isMobile) {
      return `${hour}:00`;
    }
    
    return formatTime(isoString, i18n.language);
  };
  
  // Получаем цвет в зависимости от занятости
  const getBarColor = (percentage: number): string => {
    if (percentage >= 15) return COLORS.primary; // Высокая загрузка
    if (percentage >= 8) return '#4B9D60';  // Средняя загрузка
    return '#8BC34A';                       // Низкая загрузка
  };
  
  // Подготовка дней недели для отображения с учетом перевода
  const formattedDays = peakDays.map(day => {
    // Короткие названия дней недели (Пн, Вт, Ср, Чт, Пт, Сб, Вс)
    const shortDayNames = [
      'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'
    ];
    
    const shortName = shortDayNames[day.day];
    
    return {
      ...day,
      fullName: shortName, // Используем короткие названия дней недели
      color: getBarColor(day.percentage),
      // Форматируем процент с помощью утилиты formatPercent
      formattedPercentage: formatPercent(day.percentage, 1, true, currentLocale)
    };
  });
  
  // Подготовка часов для отображения (часы уже должны быть конвертированы в часовой пояс бизнеса)
  const formattedHours = peakHours.map(hour => ({
    ...hour,
    label: formatHourLabel(hour.hour),
    color: getBarColor(hour.percentage),
    // Форматируем процент с помощью утилиты formatPercent
    formattedPercentage: formatPercent(hour.percentage, 1, true, currentLocale)
  }));
  
  // Преобразование для BarChart компонента с настройками для мобильных
  const daysData = formattedDays.map(day => ({
    name: day.fullName,
    value: day.percentage,
    color: day.color,
    tooltipLabel: day.formattedPercentage
  }));
  
  const hoursData = formattedHours.map(hour => ({
    name: hour.label,
    value: hour.percentage,
    color: hour.color,
    tooltipLabel: hour.formattedPercentage
  }));
  
  // Адаптивные настройки для графиков
  const getChartSettings = () => {
    if (isMobile) {
      return {
        height: 200,
        barSize: 15,
        margin: { top: 10, right: 10, left: 10, bottom: 10 }
      };
    } else if (isTablet) {
      return {
        height: 250,
        barSize: 20,
        margin: { top: 10, right: 15, left: 15, bottom: 15 }
      };
    } else {
      return {
        height: 300,
        barSize: 25,
        margin: { top: 10, right: 20, left: 20, bottom: 20 }
      };
    }
  };
  
  const { height, barSize, margin } = getChartSettings();
  
  // Определяем настройки сетки в зависимости от размера экрана
  const chartsPerRow = (!isMobile && displayHorizontal) ? 2 : 1;
  
  return (
    <Card padding="none" shadow="sm">
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{t('dashboard:sales.time_distribution.title')}</h3>
        <p className={styles.cardSubtitle}>{t('dashboard:sales.time_distribution.percentage')}</p>
        <p className={styles.cardSmallText}>
          {t('dashboard:sales.time_distribution.local_time')} ({businessTimezone})
        </p>
      </div>
      <div className={styles.cardContent}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p className={styles.loadingText}>{t('loading')}</p>
          </div>
        ) : (peakHours.length === 0 && peakDays.length === 0) ? (
          <div className={styles.noDataContainer}>
            <p>{t('no_data')}</p>
          </div>
        ) : (
          <Grid
            cols={1}
            tabletCols={chartsPerRow}
            gap="md"
          >
            <div>
              <h4 className={styles.chartTitle}>{t('dashboard:sales.time_distribution.peak_days')}</h4>
              {/* Используем унифицированный класс chartContainer */}
              <div className={styles.chartContainer}>
                <BarChart
                  data={daysData}
                  series={[
                    {
                      name: t('dashboard:sales.time_distribution.percentage'),
                      dataKey: 'value',
                    }
                  ]}
                  xAxisKey="name"
                  yAxisLabel={isMobile ? '' : t('dashboard:sales.time_distribution.percentage')}
                  showGrid={!isMobile} // Скрываем сетку на мобильных
                  showLegend={false}
                  borderRadius={4}
                  height={height}
                  barSize={barSize}
                  margin={margin}
                />
              </div>
            </div>
            
            <div>
              <h4 className={styles.chartTitle}>{t('dashboard:sales.time_distribution.peak_hours')}</h4>
              {/* Используем унифицированный класс chartContainer */}
              <div className={styles.chartContainer}>
                <BarChart
                  data={hoursData}
                  series={[
                    {
                      name: t('dashboard:sales.time_distribution.percentage'),
                      dataKey: 'value',
                    }
                  ]}
                  xAxisKey="name"
                  yAxisLabel={isMobile ? '' : t('dashboard:sales.time_distribution.percentage')}
                  showGrid={!isMobile} // Скрываем сетку на мобильных
                  showLegend={false}
                  borderRadius={4}
                  height={height}
                  barSize={barSize}
                  margin={margin}
                />
              </div>
            </div>
          </Grid>
        )}
      </div>
    </Card>
  );
};

export default TimeDistribution;